import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';

import Table from 'components/common/Table';

import { picklistApi } from 'api';

import {
  getPicklistListTab,
  getPicklistListColumns,
  PICKLIST_FILTER_OPTIONS,
  getPicklistBadgeColor,
} from 'constants/picklist';

import paths from 'routes/paths';
import { generatePicklistPdf } from 'utils/pdfGenerators';

const PickList = () => {
  const navigate = useNavigate();

  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const initialFilter = { warehouse: selectedWarehouse?.id };

  const handlePicklistAction = useCallback(
    async (picklistId, picklistStatus, picklistCode) => {
      try {
        if (picklistStatus === 'created') {
          await picklistApi.picklistStatusToReceiving(picklistId);
        }
        navigate(
          `${paths.picklists.process(
            tenant,
            warehouse,
            picklistId,
          )}?picklist_code=${picklistCode}`,
        );
      } catch (error) {
        toast.error('Error updating picklist status', error);
      }
    },
    [navigate, tenant, warehouse], // navigate는 stable하긴 하지만 lint는 dependency 추가 요구
  );

  const handlePrintPdf = async (picklistId, picklistCode) => {
    try {
      const response = await picklistApi.getProductBatchesOfPicklist(
        picklistId,
      );
      generatePicklistPdf(response.results, picklistCode);
    } catch (error) {
      toast.error('Error generating PDF', error);
    }
  };

  const extendedColumns = useMemo(() => {
    const actionColumn = {
      id: 'none',
      header: 'Action',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-center' },
        cellProps: { className: 'text-center' },
      },
      cell: ({ row: { original } }) => {
        const { id, picklist_status, picklist_code } = original;

        if (picklist_status === 'closed') return null;

        return (
          <Button
            variant={getPicklistBadgeColor(picklist_status)}
            size="sm"
            className="text-white"
            onClick={() =>
              handlePicklistAction(id, picklist_status, picklist_code)
            }
          >
            {picklist_status === 'created' && 'Receive Picklist'}
            {picklist_status === 'receiving' && 'Resume Picklist'}
          </Button>
        );
      },
    };

    const picklistPrintColumn = {
      id: 'pdf',
      header: '',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { id, picklist_code } = original;

        return (
          <FontAwesomeIcon
            className="cursor-pointer"
            icon="file-alt"
            onClick={() => handlePrintPdf(id, picklist_code)}
          />
        );
      },
    };

    return [
      ...getPicklistListColumns(tenant, warehouse),
      actionColumn,
      picklistPrintColumn,
    ];
  }, [tenant, warehouse, handlePicklistAction]);

  const headerActions = (
    <Button
      variant="primary"
      size="sm"
      onClick={() => navigate(paths.picklists.create(tenant, warehouse))}
    >
      Create Manual
    </Button>
  );

  return (
    <Table
      title="PickLists"
      apiService={picklistApi}
      sortable={true}
      columns={extendedColumns}
      filterOptions={PICKLIST_FILTER_OPTIONS}
      tabOptions={getPicklistListTab(initialFilter)}
      initialFilter={initialFilter}
      headerActions={headerActions}
    />
  );
};

export default PickList;
